import { HOST_READY } from "@websim/core/src/parent-api/host-ready";
import { ParentApi } from "@websim/core/src/parent-api/types";
import { connectClient, Stub } from "@websim/message-proxy/client";
import { initRuntime, ParentApiStub } from "./runtime";

// window.websim for chat-to-build sandbox previews. The builder sandbox's
// static server (bridge.mjs) injects this instead of the full runtime bundle:
// projects under construction get the same AI API they'll have once published,
// without the published runtime's analytics, fetch patching, and URL relaying
// (a sandbox preview is not the project — it must not log playtime or
// pageviews against it).
//
// Unlike the published runtime, there is no server-side __WEBSIM_DATA__ to
// bake the host origin into: the same sandbox serves previews to prod,
// localhost, and branch-preview hosts. Instead the host origin is taken from
// the parent's own host_ready message — the parent posts it targeted at this
// frame's tunnel origin, so receiving it at all proves the sender knows who
// we are, and event.origin is exactly where RPC replies must go.
const connection = new Promise<Stub<ParentApi>>((resolve) => {
  const handler = (event: MessageEvent) => {
    const host_ready = "host_ready" satisfies HOST_READY;
    if (event.data?.type === host_ready && event.source === window.parent) {
      window.removeEventListener("message", handler);
      resolve(connectClient<ParentApi>("parent", event.origin, window.parent));
    }
  };
  window.addEventListener("message", handler);
});

// Calls made before the handshake completes queue behind the connection
// promise rather than failing.
const parentApi = new Proxy({} as ParentApiStub, {
  get(_target, fn) {
    return (...args: unknown[]) =>
      connection.then((api) =>
        (api[fn as keyof ParentApi] as (...a: unknown[]) => unknown)(...args)
      );
  },
});

// Top-level (someone opened the tunnel URL directly) there is no host to
// answer, so the API would just hang — don't install it.
if (!window.websim && window.parent !== window) {
  initRuntime(parentApi);
}
